import {
  Button,
  Drawer,
  Icon,
  Panel,
  SectionHeader
} from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Separator from '~/modules/common/components/Separator';
import { useAuthContext } from '~/modules/common/contexts/auth';
import { useFeatureFlagState } from '~/modules/common/contexts/featureFlags';
import { useProducts } from '~/modules/common/contexts/products';
import { type EnterpriseMembers } from '~/modules/common/utils/billing-client/client-v2';
import { formatLongDateTime } from '~/modules/common/utils/dates';
import styles from './ViewAcceptedMemberDetails.css';

interface ViewAcceptedMemberDetailsProps {
  member: EnterpriseMembers.AcceptedMember;
  onViewDetailsClose: () => void;
  onRemoveClick: (member: EnterpriseMembers.AcceptedMember) => void;
  onEditEmailClick: (member: EnterpriseMembers.AcceptedMember) => void;
}

function ViewAcceptedMemberDetails({
  member,
  onViewDetailsClose,
  onRemoveClick,
  onEditEmailClick
}: ViewAcceptedMemberDetailsProps) {
  const { t } = useTranslation();
  const enterpriseEmailRestrictionEnabled = useFeatureFlagState(
    'restrict_enterprise_email_changes_v1'
  );
  const { user } = useAuthContext();
  const { getProductBySlug } = useProducts();
  const history = useHistory();

  const isCurrentUser = user?.id === member.user_id;

  const formattedProductActivity = (productSlug: string) => {
    const productActivity = member.user_activity.find(
      activity => activity.product_slug === productSlug
    );
    if (!productActivity) {
      return (
        <div className={styles.productActivity}>
          <Icon type="question-circle-alt" />
          {t('Activity not tracked')}
        </div>
      );
    }

    const formattedDate = formatLongDateTime(productActivity.updated_at);

    return <div>{t('Last active: {{date}}', { date: formattedDate })}</div>;
  };

  return (
    <>
      <Drawer
        open={!!member}
        onClose={onViewDetailsClose}
        position="right"
        className={styles.drawer}
      >
        <Panel className={styles.panel}>
          <Button variant="secondary" onClick={onViewDetailsClose}>
            <Icon type="close" />
            {t('Close')}
          </Button>
          <Separator />
          <SectionHeader
            heading={`${member.first_name} ${member.last_name}`}
            description={member.email}
          />
          {enterpriseEmailRestrictionEnabled && (
            <Button
              variant="secondary"
              className={styles.editEmailButton}
              onClick={() => onEditEmailClick(member)}
            >
              {t('Edit email')}
            </Button>
          )}
          <Separator />
          <SectionHeader
            heading={<h3>{t('Product access and activity')}</h3>}
          />
          <p>
            {t(
              'Not tracked means we cannot determine if or when the product was accessed. Verify usage before removing access.'
            )}
          </p>
          <div className={styles.productAccessContainer}>
            {member.product_access.map(access => (
              <Panel key={access.product_slug}>
                <h4>{getProductBySlug(access.product_slug)?.name}</h4>
                <div>{formattedProductActivity(access.product_slug)}</div>
              </Panel>
            ))}
            <Panel>
              <h4>
                {t('Axe Account')} {member.is_admin && t('(admin)')}
              </h4>
              <div>{formattedProductActivity('axe-account')}</div>
            </Panel>
          </div>
          <Button
            variant="secondary"
            onClick={() =>
              history.push(`/user-access/edit-user/${member.user_id}`)
            }
          >
            {t('Edit Access')}
          </Button>
          {!isCurrentUser && (
            <>
              <Separator />
              <SectionHeader heading={<h3>{t('Actions')}</h3>} />
              <div className={styles.actionsContainer}>
                <Button
                  variant="danger-secondary"
                  onClick={() => onRemoveClick(member)}
                >
                  {t('Remove user')}
                </Button>
              </div>
            </>
          )}
        </Panel>
      </Drawer>
    </>
  );
}

export default ViewAcceptedMemberDetails;
